import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Shield, CheckCircle, Share2, Download, Trash2, MoreVertical, Copy, Eye, Edit, Users, Key, X } from 'lucide-react';
import { Document } from '../../types';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import NotarizationButton from './NotarizationButton';
import ShareDocumentModal from './ShareDocumentModal';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { retrieveFromIPFS } from '../../lib/ipfs';
import { getEncryptionKey, decryptFile } from '../../lib/encryption';
import { useDocuments } from '../../hooks/useDocuments';

interface DocumentCardProps {
  document: Document;
  selected?: boolean;
  onSelect?: () => void;
  onNotarizationComplete?: (transactionId: string) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  selected = false,
  onSelect,
  onNotarizationComplete,
}) => {
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [showKeyRecovery, setShowKeyRecovery] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [manualKey, setManualKey] = useState('');
  const [manualIv, setManualIv] = useState('');

  const { deleteDocument, isDeletingDocument } = useDocuments();

  const getFileIcon = (type: string) => {
    return <FileText className="h-8 w-8 text-primary-500" />;
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const handleDownloadWithKeys = async (key?: string, iv?: string) => {
    setIsDownloading(true);
    try {
      toast.loading('Preparing download...', { id: 'download' });
      
      // Check if document has IPFS CID - use the correct field name
      const ipfsCid = document.ipfs_cid || document.ipfsHash;
      if (!ipfsCid) {
        throw new Error('Document not found on IPFS');
      }

      // Step 1: Retrieve encrypted file from IPFS
      toast.loading('Retrieving file from IPFS...', { id: 'download' });
      const encryptedData = await retrieveFromIPFS(ipfsCid);
      
      let fileBlob: Blob;
      
      // Use the correct field name for encryption status
      const isEncrypted = document.is_encrypted !== undefined ? document.is_encrypted : document.isEncrypted;
      
      if (isEncrypted) {
        // Step 2: Get encryption keys (either from local storage or manual input)
        toast.loading('Decrypting file...', { id: 'download' });
        let encryptionKeys;
        
        if (key && iv) {
          // Use manually provided keys
          encryptionKeys = { key, iv };
        } else {
          // Try to get from local storage
          encryptionKeys = getEncryptionKey(document.id);
        }
        
        if (!encryptionKeys) {
          // Show key recovery modal
          setShowKeyRecovery(true);
          toast.dismiss('download');
          return;
        }
        
        // Step 3: Decrypt the file
        const encryptedString = new TextDecoder().decode(encryptedData);
        const decryptedData = decryptFile({
          encryptedData: encryptedString,
          key: encryptionKeys.key,
          iv: encryptionKeys.iv
        });
        
        fileBlob = new Blob([decryptedData], { type: document.type });
      } else {
        // File is not encrypted, use as-is
        fileBlob = new Blob([encryptedData], { type: document.type });
      }
      
      // Step 4: Create download link
      toast.loading('Starting download...', { id: 'download' });
      const url = URL.createObjectURL(fileBlob);
      const downloadLink = window.document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = document.name;
      downloadLink.style.display = 'none';
      
      // Trigger download
      window.document.body.appendChild(downloadLink);
      downloadLink.click();
      window.document.body.removeChild(downloadLink);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      toast.success('Download completed!', { id: 'download' });
      
      // Close key recovery modal if it was open
      setShowKeyRecovery(false);
      setManualKey('');
      setManualIv('');
      
    } catch (error: any) {
      console.error('Download failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Download failed';
      if (error.message.includes('IPFS')) {
        errorMessage = 'Failed to retrieve file from IPFS. The file may no longer be available.';
      } else if (error.message.includes('Decryption failed')) {
        errorMessage = 'File decryption failed. Please check your encryption keys and try again.';
      } else {
        errorMessage = error.message || 'Download failed';
      }
      
      toast.error(errorMessage, { id: 'download' });
      
      // Fallback: Create a mock download for demo purposes if IPFS fails
      if (error.message.includes('IPFS') || error.message.includes('Failed to retrieve')) {
        try {
          toast.loading('Creating demo download...', { id: 'download' });
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const mockContent = `Demo Content for ${document.name}\n\nThis is a demonstration file.\nIn production, this would be the actual decrypted content from IPFS.\n\nDocument Details:\n- ID: ${document.id}\n- Size: ${formatFileSize(document.size)}\n- Type: ${document.type}\n- Encrypted: ${isEncrypted ? 'Yes' : 'No'}\n- Notarized: ${document.is_notarized || document.isNotarized ? 'Yes' : 'No'}\n- Upload Date: ${document.uploaded_at || document.uploadedAt}`;
          
          const blob = new Blob([mockContent], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const downloadLink = window.document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = `demo_${document.name}.txt`;
          window.document.body.appendChild(downloadLink);
          downloadLink.click();
          window.document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
          
          toast.success('Demo download completed! (IPFS content not available)', { id: 'download' });
        } catch (fallbackError) {
          toast.error('Download failed completely', { id: 'download' });
        }
      }
    } finally {
      setIsDownloading(false);
      setShowOptionsMenu(false);
    }
  };

  const handleDownload = async () => {
    await handleDownloadWithKeys();
  };

  const handleManualKeyDownload = async () => {
    if (!manualKey.trim() || !manualIv.trim()) {
      toast.error('Please enter both encryption key and IV');
      return;
    }
    
    await handleDownloadWithKeys(manualKey.trim(), manualIv.trim());
  };

  const handleShare = async () => {
    setShowShareModal(true);
    setShowOptionsMenu(false);
  };

  const handleDelete = async () => {
    setShowOptionsMenu(false);
    await deleteDocument(document.id, document.name);
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(document.id);
      toast.success('Document ID copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy document ID');
    }
    setShowOptionsMenu(false);
  };

  const handleViewDetails = () => {
    toast('Document details view coming soon!', { icon: 'ℹ️' });
    setShowOptionsMenu(false);
  };

  const handleEdit = () => {
    toast('Document editing coming soon!', { icon: 'ℹ️' });
    setShowOptionsMenu(false);
  };

  const optionsMenuItems = [
    {
      icon: Eye,
      label: 'View Details',
      onClick: handleViewDetails,
      color: 'text-gray-600 hover:text-gray-900'
    },
    {
      icon: Edit,
      label: 'Edit Info',
      onClick: handleEdit,
      color: 'text-gray-600 hover:text-gray-900'
    },
    {
      icon: Copy,
      label: 'Copy ID',
      onClick: handleCopyId,
      color: 'text-gray-600 hover:text-gray-900'
    },
    {
      icon: Key,
      label: 'Manual Decrypt',
      onClick: () => {
        setShowKeyRecovery(true);
        setShowOptionsMenu(false);
      },
      color: 'text-blue-600 hover:text-blue-700'
    },
    {
      icon: Users,
      label: 'Manage Access',
      onClick: () => {
        toast('Access management coming soon!', { icon: 'ℹ️' });
        setShowOptionsMenu(false);
      },
      color: 'text-gray-600 hover:text-gray-900'
    }
  ];

  // Use correct field names for display
  const isEncrypted = document.is_encrypted !== undefined ? document.is_encrypted : document.isEncrypted;
  const isNotarized = document.is_notarized !== undefined ? document.is_notarized : document.isNotarized;
  const notarizationHash = document.notarization_hash || document.notarizationHash;
  const lastModified = document.last_modified || document.lastModified;
  const tags = document.tags || [];

  return (
    <>
      <Card
        hover
        className={`
          relative overflow-hidden group
          ${selected ? 'ring-2 ring-primary-500 shadow-xl' : ''}
          ${isDeletingDocument ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        {/* Selection Checkbox */}
        <div className="absolute top-3 left-3 z-10">
          <motion.button
            onClick={onSelect}
            className={`
              w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
              ${selected 
                ? 'bg-primary-500 border-primary-500 text-white' 
                : 'border-gray-300 bg-white hover:border-primary-500'
              }
            `}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            disabled={isDeletingDocument}
          >
            {selected && <CheckCircle size={14} />}
          </motion.button>
        </div>

        {/* Options Menu */}
        <div className="absolute top-3 right-3 z-20">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              icon={MoreVertical}
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              disabled={isDeletingDocument}
              className={`
                transition-opacity duration-200
                ${showOptionsMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
              `}
            />
            
            <AnimatePresence>
              {showOptionsMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowOptionsMenu(false)}
                  />
                  
                  {/* Menu */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-8 z-20 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2"
                  >
                    {optionsMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={item.onClick}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors
                          ${item.color} hover:bg-gray-50
                        `}
                      >
                        <item.icon size={16} />
                        {item.label}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Document Preview */}
        <div className="flex flex-col items-center mb-4">
          <div className="mb-3">
            {getFileIcon(document.type)}
          </div>
          
          {/* Security Badges */}
          <div className="flex gap-2 mb-2">
            {isEncrypted && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                <Shield size={12} />
                Encrypted
              </span>
            )}
            {isNotarized && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                <CheckCircle size={12} />
                Notarized
              </span>
            )}
          </div>
        </div>

        {/* Document Info */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 truncate" title={document.name}>
            {document.name}
          </h3>
          
          <div className="text-sm text-gray-500 space-y-1">
            <p>{formatFileSize(document.size)}</p>
            <p>Modified {formatDistanceToNow(new Date(lastModified), { addSuffix: true })}</p>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 3).map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Notarization Section */}
          <div className="pt-3 border-t border-gray-100">
            <NotarizationButton
              documentId={document.id}
              fileName={document.name}
              isNotarized={isNotarized}
              notarizationHash={notarizationHash}
              onNotarizationComplete={onNotarizationComplete}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Button
            variant="ghost"
            size="sm"
            icon={Share2}
            onClick={handleShare}
            loading={isSharing}
            disabled={isDeletingDocument}
            className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            title="Share document"
          >
            Share
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            icon={Download}
            onClick={handleDownload}
            loading={isDownloading}
            disabled={isDownloading || isDeletingDocument}
            className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50"
            title="Download document"
          >
            Download
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            onClick={handleDelete}
            loading={isDeletingDocument}
            disabled={isDeletingDocument}
            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete document"
          >
            {isDeletingDocument ? 'Deleting...' : 'Delete'}
          </Button>
        </div>

        {/* Deletion Overlay */}
        {isDeletingDocument && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Deleting...</p>
            </div>
          </div>
        )}
      </Card>

      {/* Share Modal */}
      <ShareDocumentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        document={document}
      />

      {/* Key Recovery Modal */}
      <AnimatePresence>
        {showKeyRecovery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowKeyRecovery(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Key className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Manual Decryption</h3>
                      <p className="text-sm text-gray-600">Enter your encryption keys</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowKeyRecovery(false)}
                    icon={X}
                  />
                </div>

                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      <strong>File:</strong> {document.name}
                    </p>
                    <p className="text-sm text-yellow-800 mt-1">
                      Enter the encryption key and IV that were provided when you uploaded this file.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Encryption Key
                      </label>
                      <Input
                        type="text"
                        value={manualKey}
                        onChange={(e) => setManualKey(e.target.value)}
                        placeholder="Enter encryption key..."
                        className="font-mono text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Initialization Vector (IV)
                      </label>
                      <Input
                        type="text"
                        value={manualIv}
                        onChange={(e) => setManualIv(e.target.value)}
                        placeholder="Enter IV..."
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowKeyRecovery(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleManualKeyDownload}
                      loading={isDownloading}
                      disabled={!manualKey.trim() || !manualIv.trim()}
                      className="flex-1"
                    >
                      Decrypt & Download
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DocumentCard;